import { isEmptyValue, PAYMENT_METHODS } from './bookingGate';
import type { BookEventBranch, BookEventUser, PaymentMethodOption } from './bookEvent.types';
import { t } from '../../services/locale';

export function kebabCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
    .toLowerCase();
}

function cardSuffix(user: BookEventUser | null): string {
  if (user?.parent_id != null && (user.use_parent_card ?? true)) {
    return ` ${t('WEB_USE_PARENT_CARD')}`;
  }
  return user?.card?.last4 ? ` ***${user.card.last4}` : '';
}

function availableBranchMethods(
  branch: BookEventBranch | null,
  options: { excludeWallet?: boolean }
): NonNullable<BookEventBranch['paymentMethods']> {
  const methods = branch?.paymentMethods || [];
  return methods.filter((paymentMethod) => {
    if (paymentMethod.active !== true || paymentMethod.staff_only !== false) {
      return false;
    }
    if (options.excludeWallet && paymentMethod.type_id === 'WALLET') {
      return false;
    }
    return (paymentMethod.type_id || '').toLowerCase() !== PAYMENT_METHODS.DIRECT_DEBIT;
  });
}

function defaultSelected(options: PaymentMethodOption[]): PaymentMethodOption | undefined {
  const card = options.find((option) => option.key.toLowerCase() === PAYMENT_METHODS.CARD);
  return card ?? options[0];
}

export function buildCreditPaymentOptions(): {
  options: PaymentMethodOption[];
  selected: PaymentMethodOption | undefined;
} {
  const creditsValue = t('WEB_CREDITS');
  const options = [
    {
      key: PAYMENT_METHODS.CREDITS,
      value: creditsValue,
      id: kebabCase(creditsValue),
    },
  ];
  return { options, selected: options[0] };
}

export function buildBranchPaymentOptions(
  branch: BookEventBranch | null,
  user: BookEventUser | null,
  mode: 'paid' | 'combined'
): { options: PaymentMethodOption[]; selected: PaymentMethodOption | undefined } {
  const combined = mode === 'combined';
  const prefix = combined ? `${t('WEB_CREDITS')} + ` : '';
  const cardNumber = cardSuffix(user);
  const options = availableBranchMethods(branch, { excludeWallet: !combined }).map((paymentMethod) => {
    const typeId = paymentMethod.type_id || '';
    const optionValue =
      `${prefix}${t(typeId)}` +
      (typeId.toLowerCase() === PAYMENT_METHODS.CARD ? cardNumber : '');
    return {
      key: typeId,
      value: optionValue,
      id: kebabCase(optionValue),
    };
  });
  return { options, selected: defaultSelected(options) };
}

export function loadPaymentMethods(args: {
  userHasCredits: boolean;
  priceToPay: number | null;
  branch: BookEventBranch | null;
  user: BookEventUser | null;
}): { options: PaymentMethodOption[]; selected: PaymentMethodOption | undefined } {
  if (args.userHasCredits) {
    return buildCreditPaymentOptions();
  }
  if (args.priceToPay === null || args.priceToPay <= 0) {
    return { options: [], selected: undefined };
  }
  if (isEmptyValue(args.branch?.paymentMethods)) {
    return { options: [], selected: undefined };
  }
  return buildBranchPaymentOptions(args.branch, args.user, 'paid');
}
